import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getGetMyMailboxChannelQueryKey } from '@biasafe.queries/mailbox-channel/mailbox-channel';
import { useConnectMyMailbox } from '@biasafe.queries/mailbox-provider-connect/mailbox-provider-connect';
import { requestMailboxCode } from './connectFlows';
import type { MailboxChannelResponse } from '@biasafe.models';
import type { MailProvider } from './connectFlows';

/**
 * The mailbox connect action: popup consent, then the server-side exchange.
 *
 * EXTRACTED FROM `MailboxConnectCard` so the client-record card, the
 * Integrations dialog and the "Connect your tools" step run ONE flow. The OAuth
 * round trip itself stays in `requestMailboxCode` and the API; this sequences
 * it and owns the busy and error state callers draw.
 *
 * `connect` resolves to whether the mailbox is now connected and never throws.
 * The popup's own messages (blocked, cancelled, a reply that did not match) are
 * the useful ones and are kept; a generic line covers the exchange.
 */
export function useConnectMailbox() {
  const queryClient = useQueryClient();
  const connectMutation = useConnectMyMailbox();
  const [busy, setBusy] = useState<MailProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const connect = async (provider: MailProvider): Promise<boolean> => {
    setBusy(provider);
    setError(null);
    try {
      const { code, state, redirectUri } = await requestMailboxCode(provider);
      const result = await connectMutation.mutateAsync({
        provider: provider.toLowerCase(),
        data: { code, state, redirect_uri: redirectUri },
      });
      // The successful exchange already returns the authoritative channel.
      // Publish it before any follow-up read so the inbox appears immediately.
      if (result.channel) {
        queryClient.setQueryData<MailboxChannelResponse>(
          getGetMyMailboxChannelQueryKey(),
          (previous) => ({ ...previous, ...result }),
        );
      }
      void queryClient.invalidateQueries({
        queryKey: getGetMyMailboxChannelQueryKey(),
      });
      void queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === 'string' &&
          /^\/mailbox\/clients\/[^/]+\/mail$/.test(query.queryKey[0]),
      });
      toast.success('Mailbox connected');
      return true;
    } catch (err) {
      const message =
        err instanceof Error && err.message
          ? err.message
          : 'Could not connect the mailbox.';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setBusy(null);
    }
  };

  return { connect, busy, error, setError };
}
